import { FC, ChangeEvent, useState, useEffect } from 'react';
import { Box, Grid } from '@mui/material';

import { EditFormContainer, CustomerForm } from 'app/hooks/useEditForm';
import CheckoutTextField, {
  OnChangeTextField,
} from 'app/screens/Checkout/Components/CheckoutTextField';
import TextField from 'app/components/TextField';
import { isMobile } from 'utils/isMobile';
import InternationalPhoneInput from 'app/components/InternationalPhoneInput';
import ContactMethodToggle, {
  ContactMethod,
} from 'app/components/ContactMethodToggle';
import { ParsedCountry, CountryIso2 } from 'react-international-phone';
import { getCountryIso2FromDialCode } from 'utils/phoneCountryMapping';

export type FormErrors = Partial<Record<keyof CustomerForm, string>>;

interface Field {
  label: string;
  name: keyof CustomerForm;
  disabled?: boolean;
  mask?: (input: string) => string;
  type?: 'text' | 'phone' | 'contact';
}
interface Props {
  errors?: FormErrors;
  readonly?: boolean;
}

const Form: FC<Props> = props => {
  const { errors, readonly } = props;

  const { customerInfo, setCustomerInfo } = EditFormContainer.useContainer();
  const [primaryPhoneCountry, setPrimaryPhoneCountry] = useState<CountryIso2>(
    customerInfo?.countryCodeCellPhone
      ? getCountryIso2FromDialCode(customerInfo.primaryCellPhone)
      : 'us',
  );
  const [secondaryPhoneCountry, setSecondaryPhoneCountry] =
    useState<CountryIso2>(
      customerInfo?.countryCodeSecondCellPhone
        ? getCountryIso2FromDialCode(customerInfo.secondaryCellPhone)
        : 'us',
    );
  const [previousPhoneCountry, setPreviousPhoneCountry] =
    useState<CountryIso2>(primaryPhoneCountry);
  const [hasUserSelectedMethod, setHasUserSelectedMethod] =
    useState<boolean>(false);

  // Update country states when customerInfo changes

  const leftFields: Field[] = [
    { label: 'First Name', name: 'firstName', disabled: true },
    { label: 'Last Name', name: 'lastName', disabled: true },
    { label: 'Primary Cell Phone', name: 'primaryCellPhone', type: 'phone' },
    {
      label: 'Secondary Cell Phone',
      name: 'secondaryCellPhone',
      type: 'phone',
    },
  ];

  const rightFields: Field[] = [
    { label: 'E-mail Address', name: 'emailAddress', disabled: true },
    { label: 'Secondary E-mail', name: 'secondaryEmail' },
  ];

  const contactMethodField: Field = {
    label: 'Preferred Contact Method',
    name: 'preferredContactMethod',
    type: 'contact',
  };

  const onChange: OnChangeTextField = ({ name, value }) => {
    setCustomerInfo({ ...customerInfo, [name]: value });
  };

  const onDesktopChange = (
    evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    mask?: Field['mask'],
  ) => {
    const { name, value } = evt.target;

    setCustomerInfo({ ...customerInfo, [name]: mask ? mask(value) : value });
  };

  const onPhoneChange = (
    name: string,
    phone: string,
    country: ParsedCountry,
  ) => {
    const updates: Partial<CustomerForm> = { [name]: phone };

    // Set country code for primary or secondary phone
    if (name === 'primaryCellPhone') {
      updates.countryCodeCellPhone = country.dialCode;
      setPrimaryPhoneCountry(country.iso2);
      // Auto-select contact method based on primary phone country change (if user hasn't manually selected)
      if (!hasUserSelectedMethod) {
        const isIntl = country.iso2 !== 'us';
        updates.preferredContactMethod = isIntl
          ? ContactMethod.WHATSAPP
          : ContactMethod.SMS;
      }
    } else if (name === 'secondaryCellPhone') {
      updates.countryCodeSecondCellPhone = country.dialCode;
      setSecondaryPhoneCountry(country.iso2);
    }

    setCustomerInfo({ ...customerInfo, ...updates });
  };

  const onContactMethodChange = (method: ContactMethod) => {
    setHasUserSelectedMethod(true);
    setCustomerInfo({ ...customerInfo, preferredContactMethod: method });
  };

  useEffect(() => {
    const isIntl = primaryPhoneCountry !== 'us';

    if (
      primaryPhoneCountry !== previousPhoneCountry &&
      !hasUserSelectedMethod
    ) {
      setCustomerInfo(prev => ({
        ...prev,
        preferredContactMethod: isIntl
          ? ContactMethod.WHATSAPP
          : ContactMethod.SMS,
      }));
    }

    setPreviousPhoneCountry(primaryPhoneCountry);
  }, [
    primaryPhoneCountry,
    previousPhoneCountry,
    hasUserSelectedMethod,
    setCustomerInfo,
  ]);

  const renderField = (field: Field, isMobile: boolean) => {
    const value = customerInfo?.[field.name] as string;
    const error = errors?.[field.name];
    const disabled = field.disabled || readonly;

    if (field.type === 'phone') {
      const defaultCountry =
        field.name === 'primaryCellPhone'
          ? primaryPhoneCountry
          : secondaryPhoneCountry;

      if (isMobile) {
        return (
          <Grid item xs={12} key={field.name}>
            <InternationalPhoneInput
              label={field.label}
              name={field.name}
              value={value || ''}
              onChange={(phone, country) =>
                onPhoneChange(field.name, phone, country)
              }
              error={error}
              disabled={disabled}
              defaultCountry={defaultCountry}
              disableDialCodePrefill={field.name === 'secondaryCellPhone'}
            />
          </Grid>
        );
      } else {
        return (
          <Grid item key={field.name} xs={6}>
            <InternationalPhoneInput
              label={field.label}
              name={field.name}
              value={value || ''}
              onChange={(phone, country) =>
                onPhoneChange(field.name, phone, country)
              }
              error={error}
              disabled={disabled}
              defaultCountry={defaultCountry}
              disableDialCodePrefill={field.name === 'secondaryCellPhone'}
            />
          </Grid>
        );
      }
    }

    if (field.type === 'contact' && field.name === 'preferredContactMethod') {
      return (
        <Grid item xs={12} key={field.name}>
          <ContactMethodToggle
            value={typeof value === 'number' ? value : undefined}
            onChange={onContactMethodChange}
            error={error}
            disabled={disabled}
            isInternationalPhone={primaryPhoneCountry !== 'us'}
          />
        </Grid>
      );
    }

    if (isMobile) {
      return (
        <CheckoutTextField
          key={field.name}
          label={field.label}
          name={field.name}
          value={value}
          onChange={onChange}
          error={error}
          disabled={disabled}
          mask={field.mask}
        />
      );
    } else {
      return (
        <Grid
          item
          key={field.name}
          xs={field.name === 'firstName' || field.name === 'lastName' ? 6 : 12}
        >
          <TextField
            fullWidth
            key={field.name}
            label={field.label}
            name={field.name}
            value={value}
            onChange={evt => onDesktopChange(evt, field.mask)}
            helperText={error}
            error={!!error}
            disabled={disabled}
            sx={{ mb: 1 }}
          />
        </Grid>
      );
    }
  };

  return (
    <Box
      sx={{
        mt: 1,
        width: '100%',
      }}
    >
      {isMobile && (
        <Grid container spacing={2} rowSpacing={1}>
          {[...leftFields, ...rightFields, contactMethodField].map(field =>
            renderField(field, true),
          )}
        </Grid>
      )}

      {!isMobile && (
        <Box sx={{ display: 'flex', width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 5,
              pl: 4,
              pr: 6,
            }}
          >
            <Grid container spacing={4} rowSpacing={1}>
              {leftFields.map(field => renderField(field, false))}
            </Grid>
            <Box sx={{ mt: 2 }}>{renderField(contactMethodField, false)}</Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 4,
              pr: 4,
              pl: 6,
            }}
          >
            <Grid container spacing={4} rowSpacing={1}>
              {rightFields.map(field => renderField(field, false))}
            </Grid>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Form;
